// AssistantPanel — what the assistant may do with the workspace, pure.
//
// Semantic-summary consent plus the journal's own-text settings. The desktop
// BYOK API-key section left with the desktop bridge (2026-10-29); the
// Electron shell is git-recoverable.
//
// Pure/props-driven: every action reports UP.

import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Switch } from "../../Switch/Switch.js";
import { SettingsRow } from "../SettingsRow/SettingsRow.js";
import { SettingsSection } from "../SettingsSection/SettingsSection.js";

export type AssistantPanelProps = {
    // Semantic-summary consent (IDD 94442b9d) — default off, the opt-in
    // trust surface: the assistant only summarizes activity once the
    // Author explicitly turns this on, and can turn it off again any time.
    readonly summaryConsent: boolean;
    readonly onSummaryConsentChange: (consented: boolean) => void;
    // The journal's own-text settings (reading consent + remove all), right
    // under the summary consent: every "what may the assistant do with what
    // I write" choice in one place. Rendered by the journal module.
    readonly journalTexts?: ReactNode;
};

// A real Switch (controlled, no local state): the value arrives as
// `summaryConsent`, every change reports straight UP to the command dispatch.
const SUMMARY_CONSENT_SWITCH_ID = "assistant-summary-consent";
const SummaryConsentRow = ({
    summaryConsent,
    onSummaryConsentChange,
}: {
    readonly summaryConsent: boolean;
    readonly onSummaryConsentChange: (consented: boolean) => void;
}): ReactNode => {
    const { t } = useLingui();
    return (
        <SettingsRow
            label={t`Summarize my activity`}
            description={t`The assistant writes short, timestamped summaries of what's happening in your workspace, so it can pick up where you left off. Off by default — turn it on or off any time.`}
            htmlFor={SUMMARY_CONSENT_SWITCH_ID}
            control={
                <Switch
                    checked={summaryConsent}
                    onCheckedChange={onSummaryConsentChange}
                    label={t`Summarize my activity`}
                />
            }
        />
    );
};

export const AssistantPanel = ({
    summaryConsent,
    onSummaryConsentChange,
    journalTexts,
}: AssistantPanelProps) => {
    const { t } = useLingui();
    return (
        <SettingsSection
            title={t`Assistant`}
            description={t`What the assistant may do with your workspace.`}
        >
            <SummaryConsentRow
                summaryConsent={summaryConsent}
                onSummaryConsentChange={onSummaryConsentChange}
            />
            {journalTexts}
        </SettingsSection>
    );
};
